<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { $bus } from '@/common/events'
import { isDialogOpen } from '@/common/dialog'
import { toast } from '@/common/toast'
import { $router } from '@/router'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
//import { useRuntimeConfig } from '@/composables/config'
import { useSettings } from '@/composables/settings'

const route = useRoute();

const emit = defineEmits([ "reset" ]);

let warningBefore = -1; //Warn N seconds before the session expires
let idleTimeout = -1; //Max session duration

let willExpireSoonTimer = null;
let sessionExpiredTimer = null;

let willExpireSoonExpires = null;
let sessionExpiredExpires = null;

const expiryRouteName = ref("Login");

const { enabled, context } = useSessionTimer({ emit, sessionExpiresAt: null });

function start() {
  enabled.value = true;
}

function stop() {
  enabled.value = false;
}

function reset() {
  clearTimers();

  //Continue only if session timer enabled and not on login page
  if (!enabled.value || !(idleTimeout > 0) || route.name == expiryRouteName.value) {
    //Commented: too many log messages since reset() is called on each mouse move
    //console.log("[session timer] reset off", enabled.value, idleTimeout > 0, route.name == expiryRouteName.value);
    return;
  }

  //Only if warning message is enabled
  if (warningBefore > 0) {
    //Calculate duration for warning message
    let warningTimeout = idleTimeout - warningBefore;

    //Warn N seconds before the session expires
    willExpireSoonTimer = setTimeout(function() {
      //Event notification
      $bus.emit("session warning", { elapsed: warningTimeout, expiresIn: warningBefore });

      //Show message
      toast({
        type: "warning",
        text: "Seja bo kmalu potekla.", //Session will expire soon
        canClose: true,
        timeout: 3000
      });
    }, warningTimeout * 1000);

    willExpireSoonExpires = new Date().addSeconds(warningTimeout);
  }

  //Max session duration
  sessionExpiredTimer = setTimeout(function() {
    //Check if enabled
    if (!enabled.value) {
      console.log("[session timer] skip timer (not enabled)");
      return;
    }

    //Check if interrupts are allowed
    if (!canInterrupt()) {
      console.log("[session timer] skip timer (prevent interrupt)");
      return;
    }

    //Check if any prompt dialog is open
    if (isDialogOpen({ any: true })) {
      //Cannot close session, a dialog window is still open!
      console.log("[session timer] skip timer (dialog)");
      
      //Hook event when dialog window is closed
      let timer = null;
      timer = setInterval(() => {
        if (!isDialogOpen({ any: true })) {
          //Postopned session timeout since last dialog window closed!
          console.log("[session timer] expired (dialog postponed)");
          clearInterval(timer);
          onSessionExpired();
        }
      }, 200);

      return;
    }

    onSessionExpired();
  }, idleTimeout * 1000);

  sessionExpiredExpires = new Date().addSeconds(idleTimeout);
  context.value.sessionExpiresAt = sessionExpiredExpires;
}

function clearTimers() {
  //Clear timer that displays warning
  if (willExpireSoonTimer) {
    clearTimeout(willExpireSoonTimer);
    willExpireSoonTimer = null;
  }

  //Clear session expiry timer
  if (sessionExpiredTimer) {
    clearTimeout(sessionExpiredTimer);
    sessionExpiredTimer = null;
  }

  //Event notification
  $bus.emit("session extended");
}

function onSessionExpired() {
  //Stop the timer
  stop();

  //Redirect to login page
  if (expiryRouteName.value) {
    $router.push({ name: expiryRouteName.value, query: { timeout: true } });
  }

  //Event notification
  $bus.emit("session timeout", { elapsed: idleTimeout, redirect: expiryRouteName.value });

  //Show message
  toast({
    type: "warning",
    text: "Seja je potekla", //Session expired
    canClose: true,
    timeout: 10000
  });
}

context.reset = () => reset();

//TODO: useRuntimeConfig instead
//Config changed or applied
/*
$bus.on("config", (data) => {
  if (typeof data == "object" && typeof data.session == "object") {
    idleTimeout = data.session.idleTimeout; //Max session duration; if session duration is less than a minute then skip warning
    warningBefore = idleTimeout > 60 ? data.session.warningBefore : -1; //Warn N seconds before the session expires
    if (idleTimeout > 0 && enabled.value) {
      start();
    }
    else {
      stop();
    }
  }
});
*/

//const runtimeConfig = useRuntimeConfig();
const { settings } = useSettings();

function applyIdleTimeout() {
  let data = settings.value;
  console.log("[session timer] enabled =", enabled.value, "and idle timeout =", settings.value?.session?.idleTimeout);
  if (data?.session) {
    idleTimeout = data.session.idleTimeout; //Max session duration; if session duration is less than a minute then skip warning
    warningBefore = idleTimeout > 60 ? data.session.warningBefore : -1; //Warn N seconds before the session expires
    if (idleTimeout > 0 && enabled.value) {
      //start();
      console.log("[session timer] start");
      reset();
    }
    else {
      //stop();
      console.log("[session timer] stop");
      clearTimers();
    }
  }
}

watch(settings, (data) => {
  applyIdleTimeout();
});

watch(enabled, (value) => {
  applyIdleTimeout();
  /*
  if (value === true) {
    console.log("[session timer] start");
    reset();
  }
  else if (value === false) {
    console.log("[session timer] stop");
    clearTimers();
  }
  */
});

//How to detect idle time: https://stackoverflow.com/questions/667555/how-to-detect-idle-time-in-javascript
const obsrevableEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

onMounted(() => {
  applyIdleTimeout();
  window.addEventListener('load', reset, true);
  obsrevableEvents.forEach(function(name) {
    document.addEventListener(name, reset, true);
  });
});

onUnmounted(() => {
  window.removeEventListener('load', reset);
  obsrevableEvents.forEach(function(name) {
    document.removeEventListener(name, reset);
  });
});
</script>

<template>
</template>

<style scoped>
</style>
